import { combineReducers } from 'redux'
import { browserHistory } from 'utils/navigation'
import { getPasswordResetUrlToken } from 'router/locationUtils'
import { actions as notyActions } from 'layouts/ErrorBox'
import { getPasswordError, addErrorClass } from 'utils/misc'
import request, { generateNotyMessage } from 'utils/request'
import { SET_USER } from 'store/user'

const USER_ACTIVATED = 'USER_ACTIVATED'
const CLEAR_USER_TOKEN = 'CLEAR_USER_TOKEN'
const UPDATE_NEW_PASSWORD = 'UPDATE_NEW_PASSWORD'
const ADD_CHANGE_PW_ERRORS = 'ADD_CHANGE_PW_ERRORS'
const CLEAR_CHANGE_PW_ERRORS = 'CLEAR_CHANGE_PW_ERRORS'
const CLEAR_PW_ERROR = 'CLEAR_PW_ERROR'

export const addChangePwErrors = errors => {
  return {
    type: ADD_CHANGE_PW_ERRORS,
    errors,
  }
}
export const clearPwError = errorKey => {
  return {
    type: CLEAR_PW_ERROR,
    errorKey,
  }
}
export const clearChangePwErrors = () => {
  return {
    type: CLEAR_CHANGE_PW_ERRORS,
  }
}

function activateUser(token, resetPw = false) {
  return dispatch => {
    const success = jsonBody => {
      const payload = Object.assign(JSON.parse(atob(jsonBody.token.split('.')[1])), jsonBody)
      if (!payload.username || !payload.id) {
        browserHistory.push('/unauthorized')
        throw new Error('Unable to authenticate user ')
      }
      if (jsonBody.hasPassword && !resetPw) {
        browserHistory.push('/users/me')
        throw new Error('You have already set your password')
      }
      dispatch({
        type: USER_ACTIVATED,
        token: jsonBody.token,
      })
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/users/verify_token',
        body: JSON.stringify({ token }),
        failMessage: 'Unable to authenticate user.',
        success,
        authRequired: false,
        // headerOptions: {
        //   Authorization: token,
        // },
      }),
    )
  }
}

function setNewPassword(passwordState, isChangePw, reactivateSubmit) {
  return (dispatch, getState) => {
    const state = getState()
    // Expired-password change authenticates via the session Authorization header + old_password.
    // Do not put the session JWT in the body `token` field — setup_password treats that as a
    // one-time reset/activation token and returns "You've already reset your password...".
    const urlToken = getPasswordResetUrlToken(state.location)
    const verifiedToken = state.resetPassword?.userToken || state.activateUser?.userToken || null
    const token = isChangePw ? null : urlToken || verifiedToken
    const completeSubmit = () => {
      if (typeof reactivateSubmit === 'function') {
        reactivateSubmit()
      }
    }
    const passwordError = getPasswordError(passwordState.password, passwordState.password_confirmation, dispatch)
    if (!passwordError) {
      const success = () => {
        dispatch(clearChangePwErrors())
        dispatch({ type: CLEAR_USER_TOKEN })
        if (isChangePw) {
          const newLocalUserData = { ...JSON.parse(localStorage.getItem('user')) }
          newLocalUserData.expiration_message = ''
          localStorage.setItem('user', JSON.stringify(newLocalUserData))
          dispatch({
            type: SET_USER,
            payload: newLocalUserData,
          })
          browserHistory.push('/studies')
        } else browserHistory.push('/login')
        completeSubmit()
      }
      const fail = (res, content) => {
        const serverMessage = content?.message || 'Please check errors before proceeding'
        dispatch(notyActions.showError({ text: generateNotyMessage(serverMessage, false) }))
        if (res?.status >= 400 && serverMessage) {
          // "Previously used password" is about the new password; wrong current password is about old
          const isNewPasswordError =
            typeof serverMessage === 'string' &&
            /previous|has been used|reused|already used/i.test(serverMessage)
          if (res.status === 400 && !isNewPasswordError) {
            dispatch(addChangePwErrors({ oldPwSecurityErr: serverMessage }))
          } else {
            dispatch(addChangePwErrors({ pwSecurityError: serverMessage }))
          }
        }
        completeSubmit()
      }
      const body = token ? { ...passwordState, token } : { ...passwordState }
      return dispatch(
        request({
          method: 'POST',
          url: `/control/users/setup_password`,
          body: JSON.stringify(body),
          fail,
          successMessage: 'Password set successfully',
          success,
          authRequired: false,
          logoutOnUnauthorized: false,
        }),
      )
    }
    completeSubmit()
    addErrorClass(['user-password', 'user-password-confirmation'])
  }
}

function updateNewPassword(field, value) {
  return (dispatch, getState) => {
    const { resetPassword } = getState()
    const { changePwErrors } = resetPassword || {}
    if (field === 'old_password' && changePwErrors?.oldPwSecurityErr) {
      dispatch(clearPwError('oldPwSecurityErr'))
    }
    if ((field === 'password' || field === 'password_confirmation') && changePwErrors?.pwSecurityError) {
      dispatch(clearPwError('pwSecurityError'))
    }
    dispatch({
      type: UPDATE_NEW_PASSWORD,
      field,
      value,
    })
  }
}

// reducers

function changePasswordState(state = { password: '', password_confirmation: '' }, action) {
  switch (action.type) {
    case UPDATE_NEW_PASSWORD: {
      return { ...state, ...{ [action.field]: action.value } }
    }
    default: {
      return state
    }
  }
}

function userToken(state = null, action) {
  switch (action.type) {
    case USER_ACTIVATED:
      return action.token
    case CLEAR_USER_TOKEN:
      return null
    default:
      return state
  }
}

function changePwErrors(state = {}, action) {
  switch (action.type) {
    case ADD_CHANGE_PW_ERRORS:
      return { ...state, ...action.errors }
    case CLEAR_PW_ERROR:
      const newState = state
      if (newState[action.errorKey]) {
        delete newState[action.errorKey]
      }
      return newState
    case CLEAR_CHANGE_PW_ERRORS:
      return {}
    default:
      return state
  }
}

export default combineReducers({
  changePasswordState,
  changePwErrors,
  userToken,
})

export const actions = {
  activateUser,
  clearPwError,
  updateNewPassword,
  setNewPassword,
}
